<template>
  <div class="limitbee">
    <div class="banner-box" id="home" ref="home">
      <menuBox :step="5"></menuBox>
      <div class="banner-text">
        <div class="title">LimitBee</div>
        <div class="subtitle">Revolving Credit Loan</div>
      </div>
    </div>

    <div class="app-container">
      <div class="app-intro-box">
        <div class="app-header">
          <div class="app-logo">
            <img src="../assets/applogo.png" alt="LimitBee Logo" class="logo-img">
          </div>
          <div class="app-info">
            <h2 class="app-name">LimitBee - Revolving Credit Loan</h2>
            <p class="app-description">Highly flexible personal revolving credit loans to meet your funding needs anytime.</p>
          </div>
        </div>

        <div class="app-images">
          <div class="app-image-item">
            <img src="../assets/LimitBee-1.png" alt="Flexible credit limits">
          </div>
          <div class="app-image-item">
            <img src="../assets/LimitBee-2.png" alt="Safe and compliant customer service">
          </div>
          <div class="app-image-item">
            <img src="../assets/LimitBee-3.png" alt="Interest-free on idle funds">
          </div>
          <div class="app-image-item">
            <img src="../assets/LimitBee-4.png" alt="Multiple repayment methods">
          </div>
        </div>

        <div class="app-detail">
          <div class="app-long-description">
            <p>LimitBee is a flexible, revolving credit loan product designed specifically for individual users. It requires no collateral or guarantee and relies on a comprehensive credit line based on individual creditworthiness to provide convenient, efficient, and low-cost financing services.</p>

            <p>LimitBee differs from traditional lending models by focusing on intelligent credit assessment. Leveraging a mature big data risk control model, it multi-dimensionally verifies user information, consumption, and credit data to accurately determine a personalized available credit limit. The product innovatively adopts a flexible model of "credit granted first, funds disbursed later." Once a credit line is successfully obtained, there's no need for immediate borrowing; unused funds incur zero fees, essentially acting as a personal reserve fund. Fees are only incurred when the credit line is actively withdrawn, effectively avoiding unnecessary debt, easily alleviating daily cash flow pressure, and providing flexible and secure protection for personal fund management.</p>

            <p>This product operates strictly in compliance with regulations, with transparent interest and fees and clear rules. It fully protects user information and fund security, committed to creating a safe, efficient, and flexible online credit loan service experience for users.</p>
          </div>

          <div class="app-features">
            <h3>Ⅰ. Why Choose LimitBee?</h3>
            <ul class="features-list">
              <li><strong>Intelligent Assessment</strong> - Utilizing multi-dimensional data and artificial intelligence algorithms to generate personalized credit limits.</li>
              <li><strong>Security and Compliance</strong> - We employ advanced data encryption technology to strictly protect user information.</li>
              <li><strong>Simple and Convenient</strong> - Once approved, the credit limit is valid indefinitely, eliminating the need for repeated applications.</li>
              <li><strong>Flexible Use</strong> - Withdraw funds as needed, choosing your own loan amount and term.</li>
              <li><strong>Reasonable Interest</strong> - No interest is charged when no funds are used; fees are only incurred for actual use.</li>
              <li><strong>Reserve Funds</strong> - Credit limits are reserved in advance for quick access to funds in urgent situations.</li>
            </ul>
          </div>

          <div class="app-usage">
            <h3>II. How to Use?</h3>
            <div class="usage-steps">
              <div class="usage-item">
                <strong>Submit Application:</strong> Register with your real name and submit a credit application.
              </div>
              <div class="usage-item">
                <strong>Qualification Approval:</strong> Comprehensive risk control assessment using big data to determine the credit limit.
              </div>
              <div class="usage-item">
                <strong>Credit Limit Disbursement:</strong> Credit approved; the credit limit remains in your account; no fees charged if not used.
              </div>
              <div class="usage-item">
                <strong>On-Demand Withdrawal:</strong> Withdraw the corresponding amount as needed.
              </div>
              <div class="usage-item">
                <strong>On-Time Repayment:</strong> Repay principal and interest according to the rules; the credit limit is restored in real time after repayment, allowing for repeated borrowing.
              </div>
            </div>
          </div>

          <div class="app-features">
            <h3>III. LimitBee Credit Limit Introduction</h3>
            <ul class="features-list">
              <li><strong>Flexible Withdrawal Period</strong> - Users can choose their own withdrawal period and withdraw funds as needed.</li>
              <li><strong>Withdrawal Amount</strong> - Minimum INR 1,000, maximum INR 50,000.</li>
              <li><strong>Daily Interest Rate</strong> - 0.05%/day, equivalent to a maximum of 18.25%/year.</li>
              <li><strong>Withdrawal Fee</strong> - A 1% fee is charged on each withdrawal to cover related costs.</li>
            </ul>

            <div class="calculation-example">
              <h4>Withdrawal Calculation Example</h4>
              <p>If withdrawing 10,000 Indian Rupees for a term of 120 days at an annual interest rate of 18.25%:</p>
              <div class="calculation-formula">
                <div class="formula-item">Daily Interest Rate: 18.25% / 365 = 0.05%</div>
                <div class="formula-item">Daily Interest Payable: 10,000 × 0.05% = 5 Indian Rupees</div>
                <div class="formula-item">Monthly Interest Payable: 10,000 × 0.05% × 30 = 150 Indian Rupees</div>
                <div class="formula-item">Monthly Repayment: 10,000 × 0.05% × 30 + 10,000 / 120 × 30 = 2,650 Indian Rupees</div>
                <div class="formula-item">Total Interest: 10,000 × 0.05% × 120 = 600 Indian Rupees</div>
                <div class="formula-item">Total Payment: 10,000 + 600 = 10,600 Indian Rupees</div>
                <div class="formula-item">Withdrawal Fee: 10,000 INR × 1% = 100 INR</div>
                <div class="formula-item">Amount Received by User: 10,000 INR - 10,000 INR × 1% = 9,900 INR</div>
              </div>
              <p class="calculation-note">The user will receive a credit withdrawal amount of 9,900 INR, which will be repaid within 120 days, amounting to 10,600 INR. If the user repays early, the bill will be calculated based on the actual number of days the withdrawal was made.</p>
            </div>
          </div>

          <div class="app-disclaimer">
            <p><strong>You must be at least 18 years of age to use the credit analysis feature of this platform.</strong></p>
            <p>If the loan is not repaid by the due date, for example, if a user initially planned to withdraw for 30 days but needs to extend it to 45 days, the excess amount will still be charged a 0.05% extension fee per day.</p>
          </div>

          <div class="app-contact">
            <h3>IV. Contact Us</h3>
            <p>If you have any questions during use, please contact us.</p>
            <div class="contact-details">
              <div class="contact-item">
                <strong>Business Hours:</strong> Monday to Saturday, 9:30 AM to 6:30 PM.
              </div>
              <div class="contact-item">
                <strong>Email:</strong> cs@ptdaniswarateknologi.com
              </div>
              <div class="contact-item">
                <strong>Official website:</strong> https://ptdaniswarateknologi.com/
              </div>
              <div class="contact-item">
                <strong>Address:</strong> Graha Mampang Lt.3 suite 305, Jl. Mampang Prpt. Raya No.KAV.100, Desa/Kelurahan Duren Tiga, Kec. Pancoran, Kota Adm. Jakarta Selatan, Provinsi DKI Jakarta, Kode Pos: 12760.
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <footerBox></footerBox>
  </div>
</template>

<script>
  import footerBox from "@/components/footer";
  import menuBox from "@/components/menu";

  export default {
    name: "LimitBee",
    components: {
      menuBox,
      footerBox,
    },
  };
</script>

<style lang="less" scoped>
  .banner-box {
    background-image: url(../assets/swiper-3.png);
    height: 620px;
    background-size: 100% 100%;

    .banner-text {
      margin: 0 360px;
      padding-top: 100px;
      text-align: left;
      margin-top: 100px;

      .title {
        font-family: HelveticaNeue, HelveticaNeue;
        font-weight: bold;
        font-size: 80px;
        color: #333333;
      }

      .subtitle {
        font-family: HelveticaNeue;
        font-size: 28px;
        color: #6B5CE7;
        margin-top: 10px;
      }
    }
  }

  .app-container {
    background: #FFFFFF;
  }

  .app-intro-box {
    padding: 80px 360px;

    .app-header {
      display: flex;
      align-items: center;
      margin-bottom: 40px;
      padding-bottom: 30px;
      border-bottom: 2px solid #6B5CE7;

      .app-logo {
        margin-right: 30px;

        .logo-img {
          width: 80px;
          height: 80px;
          border-radius: 15px;
          box-shadow: 0px 4px 12px rgba(107, 92, 231, 0.2);
        }
      }

      .app-info {
        flex: 1;

        .app-name {
          font-family: HelveticaNeue, HelveticaNeue;
          font-weight: bold;
          font-size: 32px;
          color: #333333;
          margin-bottom: 10px;
        }

        .app-description {
          font-family: HelveticaNeue;
          font-size: 18px;
          color: #666666;
          line-height: 1.6;
          margin: 0;
        }
      }
    }

    .app-images {
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      gap: 20px;
      margin-bottom: 40px;

      .app-image-item {
        border-radius: 15px;
        overflow: hidden;
        box-shadow: 0px 6px 20px rgba(107, 92, 231, 0.15);
        transition: transform 0.3s ease, box-shadow 0.3s ease;

        &:hover {
          transform: translateY(-5px);
          box-shadow: 0px 10px 30px rgba(107, 92, 231, 0.25);
        }

        img {
          width: 100%;
          height: auto;
          display: block;
        }
      }
    }

    .app-detail {
      .app-long-description {
        margin-bottom: 40px;

        p {
          font-family: HelveticaNeue;
          font-size: 16px;
          color: #333333;
          line-height: 1.8;
          margin-bottom: 20px;
        }
      }

      .app-features,
      .app-usage,
      .app-disclaimer,
      .app-contact {
        margin-bottom: 40px;

        h3 {
          font-family: HelveticaNeue, HelveticaNeue;
          font-weight: bold;
          font-size: 24px;
          color: #6B5CE7;
          margin-bottom: 20px;
          padding-bottom: 10px;
          border-bottom: 1px solid #E5E5E5;
        }

        p {
          font-family: HelveticaNeue;
          font-size: 16px;
          color: #333333;
          line-height: 1.8;
          margin-bottom: 15px;
        }
      }

      .features-list {
        list-style: none;
        padding: 0;

        li {
          font-family: HelveticaNeue;
          font-size: 16px;
          color: #333333;
          line-height: 1.8;
          margin-bottom: 15px;
          padding-left: 20px;
          position: relative;

          &:before {
            content: "✓";
            position: absolute;
            left: 0;
            color: #6B5CE7;
            font-weight: bold;
          }

          strong {
            color: #6B5CE7;
          }
        }
      }

      .usage-steps {
        .usage-item {
          background: #F8F7FF;
          padding: 20px;
          border-radius: 10px;
          margin-bottom: 15px;
          box-shadow: 0px 2px 8px rgba(107, 92, 231, 0.08);
          font-family: HelveticaNeue;
          font-size: 16px;
          color: #333333;
          line-height: 1.6;

          strong {
            color: #6B5CE7;
          }
        }
      }

      .calculation-example {
        background: #F8F7FF;
        padding: 30px;
        border-radius: 10px;
        margin-top: 20px;

        h4 {
          font-family: HelveticaNeue, HelveticaNeue;
          font-weight: bold;
          font-size: 20px;
          color: #6B5CE7;
          margin-bottom: 15px;
        }

        .calculation-formula {
          background: #FFFFFF;
          padding: 20px;
          border-radius: 8px;
          margin: 20px 0;

          .formula-item {
            font-family: HelveticaNeue;
            font-size: 16px;
            color: #333333;
            margin-bottom: 10px;
            padding: 10px;
            background: #F8F7FF;
            border-radius: 5px;

            &:last-child {
              margin-bottom: 0;
            }
          }
        }

        .calculation-note {
          font-family: HelveticaNeue;
          font-size: 16px;
          color: #666666;
          line-height: 1.6;
          font-style: italic;
          margin-top: 20px;
        }
      }

      .app-disclaimer {
        background: #FFF9E6;
        padding: 25px;
        border-radius: 10px;
        border-left: 4px solid #6B5CE7;
      }

      .contact-details {
        background: #F8F7FF;
        padding: 25px;
        border-radius: 10px;
        box-shadow: 0px 2px 10px rgba(107, 92, 231, 0.08);

        .contact-item {
          font-family: HelveticaNeue;
          font-size: 16px;
          color: #333333;
          margin-bottom: 15px;
          line-height: 1.6;

          &:last-child {
            margin-bottom: 0;
          }

          strong {
            color: #6B5CE7;
          }
        }
      }
    }
  }

  @media (max-width: 1200px) {
    .banner-box .banner-text {
      margin: 0 200px;
    }

    .app-intro-box {
      padding: 60px 200px;
    }
  }

  @media (max-width: 768px) {
    .banner-box {
      height: 400px;

      .banner-text {
        margin: 0 20px;
        padding-top: 60px;
        margin-top: 60px;

        .title {
          font-size: 48px;
        }

        .subtitle {
          font-size: 20px;
        }
      }
    }

    .app-intro-box {
      padding: 40px 20px;

      .app-header {
        flex-direction: column;
        text-align: center;

        .app-logo {
          margin-right: 0;
          margin-bottom: 20px;
        }

        .app-info .app-name {
          font-size: 24px;
        }
      }

      .app-images {
        grid-template-columns: repeat(2, 1fr);
        gap: 15px;
      }

      .app-detail {
        .app-features h3,
        .app-usage h3,
        .app-contact h3 {
          font-size: 20px;
        }
      }
    }
  }

  @media (max-width: 480px) {
    .banner-box .banner-text .title {
      font-size: 36px;
    }

    .app-intro-box {
      .app-header .app-info .app-name {
        font-size: 20px;
      }

      .app-images {
        grid-template-columns: 1fr;
      }
    }
  }
</style>
